import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Star, User, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star Rating Component
const StarRating = ({ rating, onRatingChange, readOnly = false, size = 24 }) => {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readOnly}
          className={`transition-transform ${!readOnly && 'hover:scale-110 cursor-pointer'}`}
          onMouseEnter={() => !readOnly && setHoverRating(star)}
          onMouseLeave={() => !readOnly && setHoverRating(0)}
          onClick={() => !readOnly && onRatingChange?.(star)}
          data-testid={`star-${star}`}
        >
          <Star
            size={size}
            className={`transition-colors ${
              star <= (hoverRating || rating)
                ? 'text-yellow-500 fill-yellow-500'
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Rating Form Dialog
const RatingDialog = ({ 
  open, 
  onOpenChange, 
  restaurantId, 
  restaurantName,
  orderId,
  onSuccess 
}) => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error(language === 'pt' ? 'Por favor, selecione uma avaliação' : 'Please select a rating');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/restaurants/${restaurantId}/rate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rating,
          comment,
          order_id: orderId
        })
      });

      if (response.ok) {
        toast.success(language === 'pt' ? 'Avaliação enviada!' : 'Rating submitted!');
        onOpenChange(false);
        onSuccess?.();
        setRating(0);
        setComment('');
      } else {
        throw new Error('Failed to submit rating');
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao enviar avaliação' : 'Error submitting rating');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#004E64]">
            {language === 'pt' ? 'Avaliar' : 'Rate'} {restaurantName}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="text-center">
            <p className="text-[#9FA2B4] mb-4">
              {language === 'pt' ? 'Como foi a sua experiência?' : 'How was your experience?'}
            </p>
            <div className="flex justify-center">
              <StarRating rating={rating} onRatingChange={setRating} size={36} />
            </div>
            {rating > 0 && (
              <p className="mt-2 text-sm text-[#FF6B35]">
                {rating === 1 && (language === 'pt' ? 'Muito mau' : 'Very bad')}
                {rating === 2 && (language === 'pt' ? 'Mau' : 'Bad')}
                {rating === 3 && (language === 'pt' ? 'OK' : 'OK')}
                {rating === 4 && (language === 'pt' ? 'Bom' : 'Good')}
                {rating === 5 && (language === 'pt' ? 'Excelente!' : 'Excellent!')}
              </p>
            )}
          </div>

          <div>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={language === 'pt' ? 'Deixe um comentário (opcional)...' : 'Leave a comment (optional)...'}
              className="rounded-xl"
              rows={3}
              data-testid="rating-comment"
            />
          </div>

          <Button
            type="submit"
            disabled={loading || rating === 0}
            className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
            data-testid="submit-rating"
          >
            <Send size={18} className="mr-2" />
            {loading 
              ? (language === 'pt' ? 'Enviando...' : 'Sending...') 
              : (language === 'pt' ? 'Enviar Avaliação' : 'Submit Rating')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Restaurant Ratings List Component
const RestaurantRatings = ({ restaurantId }) => {
  const { language } = useLanguage();
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRatings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/restaurants/${restaurantId}/ratings`);
        if (response.ok) {
          const data = await response.json();
          setRatings(data);
        }
      } catch (error) {
        console.error('Error fetching ratings:', error);
      } finally {
        setLoading(false);
      }
    };

    if (restaurantId) {
      fetchRatings();
    }
  }, [restaurantId]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-24 rounded-2xl" />
        ))}
      </div>
    );
  }

  if (ratings.length === 0) {
    return (
      <div className="text-center py-8 text-[#9FA2B4]">
        {language === 'pt' ? 'Ainda não há avaliações' : 'No ratings yet'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {ratings.map((rating, index) => (
        <Card key={index} className="rounded-2xl border-[#004E64]/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF6B35]/10 flex items-center justify-center flex-shrink-0">
                <User className="text-[#FF6B35]" size={20} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[#004E64]">{rating.user_name}</span>
                  <span className="text-xs text-[#9FA2B4]">
                    {new Date(rating.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="mt-1">
                  <StarRating rating={rating.rating} readOnly size={16} />
                </div>
                {rating.comment && (
                  <p className="mt-2 text-sm text-[#9FA2B4]">{rating.comment}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export { StarRating, RatingDialog, RestaurantRatings };
